import { Grid2, IconButton, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { useAppContext } from "AppContext";
import { CommonPagination, GKTable } from "components";
import { EyeIcon } from "components/Icons";
import dayjs from "dayjs";
import { useState } from "react";
import toast from "react-hot-toast";
import { IoIosSend } from "react-icons/io";
import axiosInstance from "utils/axiosInstance";
import { extraColumnsField, numberToCurrency } from "utils/helpers";
import SendInvoiceModal from "./SendInvoiceModal";

const InvoiceList = () => {
  const {
    state: { commonFilter },
  } = useAppContext();

  // Invoice row whose send modal is open
  const [sendInvoice, setSendInvoice] = useState<any>(null);

  const requestBody = {
    take: commonFilter?.advisorInvoices?.take,
    skip: commonFilter?.advisorInvoices?.skip,
    search: commonFilter?.advisorInvoices?.search || "",
  };

  const { data: invoiceData, isFetching } = useQuery({
    queryKey: ["ADVISOR_INVOICES", requestBody],
    queryFn: () =>
      axiosInstance.get(`/super_admin/advisor-invoices/`, {
        params: requestBody,
      }),
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
  });

  const columns: GridColDef[] = [
    {
      ...extraColumnsField("Advisor Name", "advisorName", 180, 1),
    },
    {
      ...extraColumnsField("Invoice Number", "invoiceNumber", 150, 1),
    },
    {
      ...extraColumnsField("Invoice Date", "invoiceDate", 130, 1),
      valueFormatter: (params) =>
        params?.value ? dayjs(params.value).format("DD MMM YYYY") : "-",
    },
    {
      ...extraColumnsField("Billing Date", "billingDate", 130, 1),
      valueFormatter: (params) =>
        params?.value ? dayjs(params.value).format("DD MMM YYYY") : "-",
    },
    {
      ...extraColumnsField("Amount", "amount", 130, 1),
      valueFormatter: (params) => numberToCurrency(params?.value) || "-",
    },
    {
      ...extraColumnsField("Action", "action", 110, 0),
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Grid2 container gap={0.5} justifyContent="center">
          <Tooltip title="View Invoice" arrow>
            {/* span keeps the tooltip working when the button is disabled */}
            <span>
              <IconButton
                size="small"
                disabled={!params.row.invoicePdf}
                onClick={(event) => {
                  event.stopPropagation();
                  window.open(
                    params.row.invoicePdf,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
              >
                <EyeIcon width={18} height={18} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Send Invoice" arrow>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setSendInvoice(params.row);
              }}
            >
              <IoIosSend size={18} />
            </IconButton>
          </Tooltip>
        </Grid2>
      ),
    },
  ];

  const cusPagination = () => {
    return (
      <CommonPagination
        name="advisorInvoices"
        totalCount={invoiceData?.data?.totalCount || 0}
      />
    );
  };

  return (
    <>
      <GKTable
        loading={isFetching}
        columns={columns}
        rows={invoiceData?.data?.data || []}
        name="advisorInvoices"
        CustomPagination={!isFetching && cusPagination}
      />
      {sendInvoice && (
        <SendInvoiceModal
          open={!!sendInvoice}
          setOpen={() => setSendInvoice(null)}
          invoice={sendInvoice}
        />
      )}
    </>
  );
};

export default InvoiceList;
